import React, { useState } from 'react';
import { Search, Loader, BookOpen, FileText, AlertCircle } from 'lucide-react';
import { apiService } from '../services/api';
import ReactMarkdown from 'react-markdown';

interface DeepResearchProps {
  onClose: () => void;
}

const DeepResearch: React.FC<DeepResearchProps> = ({ onClose }) => {
  const [query, setQuery] = useState('');
  const [researchType, setResearchType] = useState<'comprehensive' | 'focused' | 'literature_review'>('focused'); // Default to focused for optimal performance
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [progressStatus, setProgressStatus] = useState<string>('');
  const [progressPercent, setProgressPercent] = useState<number>(0);

  const exampleQueries = [
    "Best treatment approach for Class II malocclusion with deep overbite in adolescents",
    "Invisalign effectiveness for moderate crowding cases",
    "Temporary anchorage devices success rates and complications",
    "Compare functional appliances vs fixed appliances for Class II correction",
    "Long-term stability of orthognathic surgery outcomes",
    "Evidence for early vs late treatment of anterior crossbite"
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;

    setIsLoading(true);
    setError(null);
    setResult(null);
    setProgressStatus('');
    setProgressPercent(0);

    try {
      const response = await apiService.performDeepResearch(
        query, 
        researchType,
        (status: string, progress?: number) => {
          setProgressStatus(status);
          if (progress !== undefined) {
            setProgressPercent(progress);
          }
        }
      );
      
      if (response.action === 'error') {
        // Check if it's a timeout error
        if (response.metadata?.error === 'timeout') {
          setError('Research synthesis timed out. Try simplifying your query or selecting "Focused" research type for faster results.');
        } else {
          setError(response.response || 'An error occurred');
        }
      } else {
        setResult(response);
      }
    } catch (err: any) {
      setError('Failed to perform research. Please try again.');
      console.error('Research error:', err);
    } finally {
      setIsLoading(false);
      setProgressStatus('');
      setProgressPercent(0);
    }
  };

  const selectExample = (example: string) => {
    setQuery(example);
  };

  return (
    <div className="h-full bg-white flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-medical-200 px-6 py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <BookOpen className="w-8 h-8 text-primary-600" />
              <div>
                <h1 className="text-2xl font-semibold text-medical-900">Deep Research</h1>
                <p className="text-medical-600">Evidence-based research from peer-reviewed orthodontic journals</p>
              </div>
            </div>
          </div>
        </div>
      </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto bg-medical-50">
          <div className="max-w-4xl mx-auto p-6">
            {!result && (
              <>
              {/* Search Form */}
              <form onSubmit={handleSubmit} className="mb-6 bg-white rounded-lg p-6 shadow-sm border border-medical-200">
                <p className="text-sm text-medical-500 mb-4">
                  💡 Tip: Use "Focused" for quick answers (~15 sec) or "Comprehensive" for detailed analysis
                </p>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-medical-700 mb-2">
                    Research Question
                  </label>
                  <div className="relative">
                    <textarea
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Enter your orthodontic research question..."
                      className="w-full px-4 py-3 border border-medical-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                      rows={3}
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-medical-700 mb-2">
                    Research Type
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => setResearchType('focused')}
                      className={`p-3 rounded-lg border transition-colors ${
                        researchType === 'focused'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-medical-300 hover:border-medical-400'
                      }`}
                      disabled={isLoading}
                    >
                      <div className="font-medium">
                        Focused
                        <span className="ml-1 text-xs text-green-600 font-normal">• Recommended</span>
                      </div>
                      <div className="text-xs mt-1">~15 sec • Quick answers</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setResearchType('literature_review')}
                      className={`p-3 rounded-lg border transition-colors ${
                        researchType === 'literature_review'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-medical-300 hover:border-medical-400'
                      }`}
                      disabled={isLoading}
                    >
                      <div className="font-medium">Literature Review</div>
                      <div className="text-xs mt-1">Academic synthesis</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setResearchType('comprehensive')}
                      className={`p-3 rounded-lg border transition-colors ${
                        researchType === 'comprehensive'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-medical-300 hover:border-medical-400'
                      }`}
                      disabled={isLoading}
                    >
                      <div className="font-medium">Comprehensive</div>
                      <div className="text-xs mt-1">~19 sec • Full analysis</div>
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !query.trim()}
                  className="w-full medical-button flex items-center justify-center space-x-2"
                >
                  {isLoading ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Researching...</span>
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4" />
                      <span>Perform Research</span>
                    </>
                  )}
                </button>
              </form>

              {/* Example Queries */}
              <div className="bg-medical-50 rounded-lg p-4">
                <h3 className="font-medium text-medical-900 mb-3">Example Queries:</h3>
                <div className="space-y-2">
                  {exampleQueries.map((example, index) => (
                    <button
                      key={index}
                      onClick={() => selectExample(example)}
                      className="text-left w-full p-2 rounded hover:bg-medical-100 transition-colors text-sm text-medical-700"
                      disabled={isLoading}
                    >
                      • {example}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader className="w-12 h-12 text-primary-600 animate-spin mb-4" />
              <p className="text-medical-600 font-medium">Performing deep research...</p>
              
              {/* Progress Bar */}
              {progressPercent > 0 && (
                <div className="w-full max-w-md mt-4 mb-2">
                  <div className="h-2 bg-medical-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary-600 transition-all duration-300 rounded-full"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  <p className="text-center text-sm text-medical-500 mt-1">{progressPercent}%</p>
                </div>
              )}
              
              {/* Status Message */}
              {progressStatus && (
                <p className="text-sm text-medical-600 mt-2 font-medium">{progressStatus}</p>
              )}
              
              <p className="text-sm text-medical-500 mt-2">This may take up to 30-60 seconds for complex queries</p>
              
              {!progressStatus && (
                <p className="text-xs text-medical-400 mt-4">Searching peer-reviewed orthodontic journals...</p>
              )}
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <p className="text-red-800 font-medium">Research Error</p>
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Results */}
          {result && !isLoading && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg border border-medical-200 p-6 mb-4">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <BookOpen className="w-6 h-6 text-primary-600" />
                    <div>
                      <h3 className="text-lg font-semibold text-medical-900">Research Results</h3>
                      <p className="text-sm text-medical-600 mt-1">{query}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setResult(null);
                      setQuery('');
                    }}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    New Research
                  </button>
                </div>
              </div>

              {/* Research Content in Styled Container */}
              <div className="bg-white rounded-lg border border-medical-200 p-6">
                <div className="prose prose-base max-w-none">
                  <ReactMarkdown
                    components={{
                      h1: ({ children }: any) => <h1 className="text-2xl font-bold text-medical-900 mb-4 mt-6 first:mt-0 border-b border-medical-200 pb-2">{children}</h1>,
                      h2: ({ children }: any) => <h2 className="text-xl font-bold text-medical-900 mb-3 mt-6 first:mt-0">{children}</h2>,
                      h3: ({ children }: any) => <h3 className="text-lg font-semibold text-medical-800 mb-3 mt-5">{children}</h3>,
                      h4: ({ children }: any) => <h4 className="text-base font-semibold text-medical-800 mb-2 mt-4">{children}</h4>,
                      p: ({ children }: any) => <p className="mb-4 text-medical-700 leading-relaxed">{children}</p>,
                      ul: ({ children }: any) => <ul className="list-disc pl-6 mb-4 space-y-2">{children}</ul>,
                      ol: ({ children }: any) => <ol className="list-decimal pl-6 mb-4 space-y-2">{children}</ol>,
                      li: ({ children }: any) => (
                        <li className="text-medical-700 leading-relaxed">
                          <span className="block">{children}</span>
                        </li>
                      ),
                      strong: ({ children }: any) => <strong className="font-semibold text-medical-900">{children}</strong>,
                      em: ({ children }: any) => <em className="italic text-medical-700">{children}</em>,
                      blockquote: ({ children }: any) => (
                        <blockquote className="border-l-4 border-primary-300 pl-4 py-2 my-4 bg-primary-50 rounded-r">
                          {children}
                        </blockquote>
                      ),
                      code: ({ inline, children }: any) => 
                        inline ? (
                          <code className="bg-medical-100 px-1.5 py-0.5 rounded text-sm font-mono text-medical-800">{children}</code>
                        ) : (
                          <pre className="bg-medical-900 text-white p-4 rounded-lg overflow-x-auto mb-4">
                            <code className="text-sm font-mono">{children}</code>
                          </pre>
                        ),
                      hr: () => <hr className="my-6 border-medical-200" />,
                    }}
                  >
                    {result.response}
                  </ReactMarkdown>
                </div>
              </div>

              {result.metadata && (
                <div className="bg-medical-50 rounded-lg p-5 mt-6 border border-medical-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Sources */}
                    <div className="flex items-center space-x-2">
                      <FileText className="w-4 h-4 text-medical-600" />
                      <span className="text-sm">
                        <span className="font-medium text-medical-700">Sources:</span>{' '}
                        <span className="text-medical-600">{result.metadata.sources_count || 0} peer-reviewed</span>
                      </span>
                    </div>
                    
                    {/* Research Type */}
                    <div className="text-sm">
                      <span className="font-medium text-medical-700">Type:</span>{' '}
                      <span className="text-medical-600">
                        {(result.metadata.research_type || researchType).replace('_', ' ')}
                      </span>
                      {result.metadata.from_cache && (
                        <span className="text-medical-500 italic ml-2">(cached)</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Topics */}
                  {result.metadata.aspects_researched && result.metadata.aspects_researched.length > 0 && (
                    <div className="mt-3 text-sm">
                      <span className="font-medium text-medical-700">Topics:</span>{' '}
                      <span className="text-medical-600">
                        {result.metadata.aspects_researched.join(' • ')}
                      </span>
                    </div>
                  )}
                  
                  {/* Research ID */}
                  {result.metadata.research_id && (
                    <div className="mt-3 text-xs text-medical-500 font-mono">
                      ID: {result.metadata.research_id}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          </div>
        </div>
    </div>
  );
};

export default DeepResearch;